import React, { useEffect, useRef, useState } from 'react';
import { Button, DatePicker, InputNumber } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/pl';
import plLocale from 'antd/locale/pl_PL';
import { useTranslation } from 'react-i18next';
import type { WorkflowInvoiceDraft } from '../../../types/workflowInvoice';
import { fetchInvoiceDatedRate } from '../api/invoiceExchangeRate';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../../../utils/polishNumberInput';

export default function InvoiceExchangeRateField({ value, patch, disabled }: {
  value: WorkflowInvoiceDraft; patch: (next: Partial<WorkflowInvoiceDraft>) => void; disabled: boolean;
}) {
  const { t } = useTranslation('invoices');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const current = useRef({ value, patch, disabled }); current.current = { value, patch, disabled };
  const generation = useRef(0);
  const date = value.exchangeRateDate?.slice(0, 10) ?? (value.issueDate ? value.issueDate.slice(0, 10) : null);
  const attempted = useRef<string | undefined>(undefined);
  const load = async (requestedDate = date) => {
    if (!value.currency || !requestedDate) return;
    const request = ++generation.current;
    const original = value;
    setLoading(true); setFailed(false);
    try {
      const snapshot = await fetchInvoiceDatedRate(value.currency, requestedDate);
      const latest = current.current;
      if (generation.current !== request || latest.disabled || latest.value.id !== original.id || latest.value.status !== original.status || latest.value.currency !== original.currency
        || latest.value.exchangeRateToPLN !== original.exchangeRateToPLN
        || latest.value.exchangeRateDate !== original.exchangeRateDate || latest.value.issueDate !== original.issueDate) return;
      latest.patch({ exchangeRateToPLN: snapshot.rate, exchangeRateDate: requestedDate, exchangeRateSnapshot: snapshot });
    } catch { if (request === generation.current) setFailed(true); }
    finally { if (request === generation.current) setLoading(false); }
  };
  useEffect(() => {
    generation.current += 1;
    setLoading(false); setFailed(false);
  }, [value.id, value.currency, value.issueDate, value.status, disabled]);
  useEffect(() => { setSelectedDate(null); }, [value.id, value.currency]);
  useEffect(() => {
    const key = `${value.id}:${value.currency}:${date}`;
    if (!disabled && value.currency && value.currency !== 'PLN' && date && value.exchangeRateToPLN == null && attempted.current !== key) {
      attempted.current = key; void load();
    }
    // User edits and late responses are checked against current above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.id, value.currency, date, value.exchangeRateToPLN, disabled]);
  useEffect(() => () => { generation.current += 1; attempted.current = undefined; }, []);
  if (value.currency === 'PLN') return <div className="order-wizard__value-box">1 PLN = 1 PLN</div>;
  return <div className="invoice-fx">
    <div className="invoice-fx-input">
      <InputNumber aria-label={t('invoices:workflow.fields.exchangeRate')} value={value.exchangeRateToPLN ?? null} disabled={disabled} controls={false}
        onChange={rate => { generation.current += 1; attempted.current = `${value.id}:${value.currency}:${value.issueDate?.slice(0, 10) ?? null}`; setLoading(false); setFailed(false); patch({ exchangeRateToPLN: rate, exchangeRateDate: null, exchangeRateSnapshot: null }); }} {...POLISH_DECIMAL_INPUT_NUMBER_PROPS} />
    </div>
    <label className="invoice-fx-date-label">
      <span>{t('invoices:workflow.review.rateDate')}</span>
      <div className="invoice-fx-date">
        <DatePicker locale={plLocale.DatePicker} aria-label={t('invoices:workflow.review.rateDate')} format="DD.MM.YYYY" allowClear={false}
          value={selectedDate || date ? dayjs(selectedDate ?? date) : null} disabled={disabled}
          disabledDate={day => day.isAfter(dayjs(), 'day') || day.isBefore('2002-01-02', 'day')}
          onChange={day => { generation.current += 1; setLoading(false); setFailed(false); setSelectedDate(day?.format('YYYY-MM-DD') ?? null); }} />
        <Button loading={loading} disabled={disabled || !value.currency || !(selectedDate ?? date)}
          onClick={() => void load(selectedDate ?? date)}>{t('invoices:workflow.review.loadRate')}</Button>
      </div>
    </label>
    <span className="invoice-field-hint">{value.exchangeRateSnapshot && value.exchangeRateSnapshot.rate === value.exchangeRateToPLN
      ? t('invoices:workflow.review.rateSource', { date: dayjs(value.exchangeRateSnapshot.effectiveDate).format('DD.MM.YYYY'), table: value.exchangeRateSnapshot.tableNumber })
      : t('invoices:workflow.review.rateUnknown')}</span>
    {value.exchangeRateSnapshot && value.exchangeRateSnapshot.rate === value.exchangeRateToPLN
      && value.exchangeRateSnapshot.effectiveDate.slice(0, 10) < value.exchangeRateSnapshot.requestedDate.slice(0, 10)
      && <span className="invoice-field-hint">{t('invoices:workflow.review.rateEarlier', { date: dayjs(value.exchangeRateSnapshot.effectiveDate).format('DD.MM.YYYY') })}</span>}
    {failed && <span role="alert" className="invoice-field-error">{t('invoices:workflow.review.rateFailed')}</span>}
  </div>;
}
